import { useStyle } from "./style";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import { OidcProviderButtons } from "@/widgets/auth/oidc-provider-buttons";
import { fetchOidcInviteInfo } from "@/services/api-service/auth/oidc-api";
import { NativeSignupForm } from "./native-signup-form";
import { fetchPrintHouseBrand } from "@/contexts/theme";

/** Non-HP invitees only; HP is handed off to Cognito before this renders. */
const InviteAcceptanceLeftSide = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { token } = router.query;

  const [errorMsg, setErrorMsg] = useState("");
  const [loginLogo, setLoginLogo] = useState<string | null>(null);
  // Undefined while we resolve the token; new invitees get "Sign up with …" buttons.
  const [isNewUser, setIsNewUser] = useState<boolean | undefined>(undefined);
  const [inviteEmail, setInviteEmail] = useState<string | undefined>(undefined);

  const inviteToken = typeof token === "string" ? token : undefined;

  useEffect(() => {
    if (!router.isReady) return;
    if (!token) {
      setErrorMsg(t("invite.invalidToken"));
      return;
    }

    let mounted = true;
    fetchPrintHouseBrand().then((brand) => {
      if (mounted && brand?.loginLogo) {
        setLoginLogo(brand.loginLogo);
      }
    });

    return () => {
      mounted = false;
    };
  }, [token, router.isReady, t]);

  useEffect(() => {
    if (!inviteToken) return;
    let mounted = true;
    fetchOidcInviteInfo(inviteToken)
      .then((info) => {
        if (mounted) {
          setIsNewUser(info.isNewUser);
          setInviteEmail(info.email);
        }
      })
      .catch(() => {
        // Fail open to sign-up so a new invitee is never blocked on a login screen.
        if (mounted) setIsNewUser(true);
      });
    return () => {
      mounted = false;
    };
  }, [inviteToken]);

  const { clasess } = useStyle();

  return (
    <div style={clasess.leftContainer}>
      {loginLogo ? (
        <Image
          alt="logo"
          src={loginLogo}
          width={180}
          height={180}
          style={{
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      ) : (
        <div></div>
      )}
      <div style={clasess.loginContainer}>
        <div style={clasess.loginLbl}>{t("invite.acceptInvitation")}</div>
        <div style={clasess.descriptionLbl}>
          {isNewUser
            ? "Choose how you'd like to sign up to accept your invitation."
            : t("invite.chooseProviderDescription") !==
              "invite.chooseProviderDescription"
            ? t("invite.chooseProviderDescription")
            : "Choose how you'd like to sign in to accept your invitation."}
        </div>

        <div style={{ width: "100%", marginTop: 16 }}>
          {inviteToken && isNewUser !== undefined ? (
            isNewUser ? (
              // New invitee: Cognito self sign-up is disabled, so GoMake uses a native
              // OTP + password sign-up; Google/HP still federate through the Hosted UI.
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  width: "100%",
                }}
              >
                <NativeSignupForm
                  inviteToken={inviteToken}
                  defaultEmail={inviteEmail}
                  onError={setErrorMsg}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    color: "#B1B1B1",
                    fontSize: 13,
                  }}
                >
                  <span style={{ flex: 1, height: 1, background: "#E0E0E0" }} />
                  {t("common.or") !== "common.or" ? t("common.or") : "or"}
                  <span style={{ flex: 1, height: 1, background: "#E0E0E0" }} />
                </div>
                <OidcProviderButtons
                  inviteToken={inviteToken}
                  isSignup
                  excludeProviders={["gomake"]}
                  onError={setErrorMsg}
                />
              </div>
            ) : (
              <OidcProviderButtons
                inviteToken={inviteToken}
                isSignup={false}
                onError={setErrorMsg}
              />
            )
          ) : null}
        </div>

        <div style={clasess.errorMsgStyle}>{errorMsg}</div>
      </div>
    </div>
  );
};
export { InviteAcceptanceLeftSide };
